<script setup>
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Spin, message, Button } from 'ant-design-vue';
import apiService from '@/api/apiService';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { injectPrivateRoutesByPermission } from '@/router/injectPrivateRoutesByPermission';
import { goToGrowthSystemHome, resolveGrowthSystemHomeUrl } from '@/utils/growthSystemNav';

const route = useRoute();
const router = useRouter();
const adminStore = useAdminInfo();
const errorText = ref('');
const growthHomeUrl = resolveGrowthSystemHomeUrl(import.meta.env.VITE_GROWTH_SYSTEM_URL);

/** 只接受相對路徑，避免 open redirect */
function safeReturnPath(raw) {
    if (typeof raw !== 'string' || !raw) return '';
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('#')) return '';
    return raw;
}

onMounted(async () => {
    const code = route.query.code;
    if (!code) {
        // 常見原因：成長端錯誤回應導向了裸 callback，或 bookmark / 返回鍵殘影
        errorText.value = '登入連結無效（缺少兌換碼）。請從世界樹文明重新進入諮詢工作台。';
        return;
    }

    try {
        const res = await apiService.POST('/api/admin/sso/exchange', { code });
        if (res.status !== 1) {
            errorText.value = res.message || 'SSO 登入失敗';
            return;
        }

        localStorage.setItem('accessToken', res.data.access_token);
        adminStore.setAdminInfo(res.data.manager);
        await injectPrivateRoutesByPermission(true);

        // 由 booking／成長系統 SSO 進來時若帶 return_path，僅決定進站落地頁（非「回成長系統」）
        // 使用 replace，避免 callback URL 堆疊造成返回地獄
        const next = safeReturnPath(route.query.return_path) || adminStore.defaultHomePath;
        await router.replace(next);
    } catch (e) {
        errorText.value = e?.message || 'SSO 登入失敗';
        message.error(errorText.value);
    }
});

const backToGrowth = () => {
    if (!goToGrowthSystemHome(import.meta.env.VITE_GROWTH_SYSTEM_URL)) {
        router.replace('/login');
    }
};
</script>

<template>
    <div class="sso-callback">
        <Spin v-if="!errorText" tip="正在登入諮詢後台…" />
        <div v-else class="error-box">
            <p class="error">{{ errorText }}</p>
            <Button v-if="growthHomeUrl" type="primary" class="mt-4" @click="backToGrowth">
                ‹ 回世界樹文明
            </Button>
            <Button v-else type="default" class="mt-4" @click="router.replace('/login')">
                前往登入頁
            </Button>
        </div>
    </div>
</template>

<style scoped>
.sso-callback {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
}
.error-box {
    max-width: 28rem;
    padding: 1.5rem;
    text-align: center;
}
.error {
    color: #cf1322;
    margin: 0;
    line-height: 1.6;
}
.mt-4 {
    margin-top: 1rem;
}
</style>
